/* eslint-disable unicorn/filename-case -- TODO rename later */
import { type Mutation, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { reportAsyncError } from "~/shared/errors/report-async-error";
import { isNonRetryableClientError } from "~/utils/sdk-errors";

export interface AppMutationMeta {
  /** @deprecated Use `triggerGlobalInvalidation: false` at the mutation call site. */
  skipGlobalInvalidation?: boolean;
  /** Set to `false` to opt out. Undefined falls through so callers default to global invalidation. */
  triggerGlobalInvalidation?: boolean;
}

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: AppMutationMeta;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Non-retryable 4xx (bad ids, invalid params, permission failures) can't
      // recover on retry. Transient 4xx (408, 425, 429) and 5xx are still retried.
      retry: (failureCount, error) => {
        if (isNonRetryableClientError(error)) return false;
        return failureCount < 3;
      },
    },
  },
  // Catches background failures the UI never surfaces: prefetch storms, stale
  // background refetches, useQuery results no observer is reading. Pair with
  // observer-level handling for mutations users actually wait on.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.getObserversCount() > 0) return;
      // queryHash is a stable, non-PII identifier. The raw queryKey can carry
      // user-entered search terms and token-like values (e.g. content-token salts),
      // so it must not be forwarded to Sentry.
      reportAsyncError("query-cache", error, { queryHash: query.queryHash });
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      // === false: undefined falls through so callers default to global invalidation
      if (mutation.meta?.skipGlobalInvalidation || mutation.meta?.triggerGlobalInvalidation === false) return;
      logInvalidation(mutation);
      queryClient.invalidateQueries();
    },
  }),
});

function logInvalidation(mutation: Mutation<unknown, unknown, unknown, unknown>): void {
  if (import.meta.env.DEV && localStorage.getItem("debug:invalidation") === "1") {
    const observers = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => q.getObserversCount() > 0);
    console.info("[mutation→invalidate]", {
      mutationKey: mutation.options.mutationKey,
      observerCount: observers.length,
      observerKeys: observers.map((q) => q.queryKey),
    });
  }
}
